import { List } from "@/components/atoms/list";
import { ItemDetailsDialogMobile } from "@/components/molecules/itemDetailsDialogMobile";
import {
    SupervisorAgentDto,
    SupervisorListMobileProps,
    SupervisorTableColumnNames,
} from "./types";
import { useMemo, useState, useCallback, useEffect } from "react";
import { SparkleFilled } from "@fluentui/react-icons";
import { UserPresence } from "../userPresence/UserPresence";
import { RefreshAgentButton } from "@/components/atoms/refreshAgent";
import { Text } from "@fluentui/react-components";
import Timer from "@/components/atoms/timer/Timer";
import { Availabilities } from "../currentUserPresence";
import { useStyle } from "./style";
import { ItemProp } from "@/components/atoms/itemDetailsMobile";
import { useIntl } from "react-intl";
import { SkillList } from "@/components/atoms/skillList";
import { formatDate } from "@/common/helpers/dateHelper";
import { translateActivity } from "@/common/helpers/presenceHelper";

export const SupervisorListMobile = (props: SupervisorListMobileProps) => {
    const {
        supervisorTableItems,
        supervisorShowStatusTimer,
        getHasClientStatusIcon,
        getJoinedToMultipleQueuesCellContent,
        workloadMenu,
        agentTimer,
    } = props;

    const classes = useStyle();
    const intl = useIntl();

    const [isDetailsDialogOpen, setIsDetailsDialogOpen] =
        useState<boolean>(false);
    const [selectedAgentId, setSelectedAgentId] = useState<string | undefined>();
    const [itemDetailsList, setItemDetailsList] = useState<ItemProp[]>();

    const createDetail = (
        labelId: string,
        mainValue: any,
        secondaryValue?: any
    ) => ({
        label: intl.formatMessage({ id: `Supervisor.${labelId}` }),
        mainValue,
        secondaryValue,
    });

    useEffect(() => {
        if (isDetailsDialogOpen && selectedAgentId) {
            const item = supervisorTableItems?.find((a) => a.id === selectedAgentId);
            if (item) {
                setItemDetailsList([
                    createDetail(SupervisorTableColumnNames.Agent, item.name),
                    createDetail(
                        SupervisorTableColumnNames.Skills,
                        item.skills && item.skills.length > 0 ? (
                            <SkillList
                                skillList={item.skills?.map((x) => ({
                                    id: x.skillName,
                                    name: x.skillName,
                                    weight: x.skillLevel,
                                }))}
                            />
                        ) : (
                            "-"
                        )
                    ),
                    createDetail(
                        SupervisorTableColumnNames.LastHandled,
                        item.lastHandled ? formatDate(item.lastHandled) : ""
                    ),
                    createDetail(SupervisorTableColumnNames.Workload, workloadMenu(item)),
                    createDetail(
                        SupervisorTableColumnNames.StatusTimer,
                        agentTimer(item)
                    ),
                    createDetail(
                        SupervisorTableColumnNames.Reason,
                        translateActivity(item.reason, intl)
                    ),
                    createDetail(
                        SupervisorTableColumnNames.HasClient,
                        getHasClientStatusIcon(item.hasClient)
                    ),
                    createDetail(
                        SupervisorTableColumnNames.JoinedTo,
                        getJoinedToMultipleQueuesCellContent(item)
                    ),
                ]);
            }
        }
    }, [supervisorTableItems, isDetailsDialogOpen, selectedAgentId]);

    const openActiveInteractionDetailsDialog = useCallback(
        (item: SupervisorAgentDto) => {
            setSelectedAgentId(item.id);
            setIsDetailsDialogOpen(true);
        },
        []
    );

    const listItems = useMemo(() => {
        return supervisorTableItems.map((agent) => {
            return {
                key: agent.id,
                media: (
                    <UserPresence
                        queueIds={agent.queues?.map((q) => q.id)}
                        avatarView={{
                            agentState: {
                                id: agent.id,
                                fullName: agent.name,
                                availability: agent.presence?.availability,
                                activity: agent.presence?.activity,
                            },
                            size: 32,
                            photoId: agent.isAiAgent ? undefined : agent.id,
                        }}
                        isDisabled={!agent.isSupervisorOnAtLeastOneQueue || agent.isAiAgent}
                        allowActionsWhenDisabled={agent.isAiAgent && agent.isSupervisorOnAtLeastOneQueue}
                        additionalActions={[
                            <RefreshAgentButton agentToRefreshId={agent.id} />,
                        ]}
                        hideLock
                        hideReasons
                    />
                ),
                header: agent.isAiAgent ? <>{agent.name}<SparkleFilled /></> : agent.name,
                headerMedia: (
                    <Text>
                        {supervisorShowStatusTimer &&
                        agent.presence?.availability !== Availabilities.Offline &&
                        !!agent.presence?.updatedAt ? (
                            <Timer startDate={agent.presence?.updatedAt} />
                        ) : (
                            ""
                        )}
                    </Text>
                ),
                onClick: () => {
                    openActiveInteractionDetailsDialog(agent);
                },
            };
        });
    }, [
        supervisorTableItems,
        supervisorShowStatusTimer,
        openActiveInteractionDetailsDialog,
    ]);

    return (
        <>
            <div style={{ marginTop: "20px" }}>
                <List items={listItems} classesName={{ item: classes.listItem }} />
            </div>
            <ItemDetailsDialogMobile
                itemProps={itemDetailsList}
                titleKey={"MobileView.AgentDetails"}
                open={isDetailsDialogOpen}
                setOpen={setIsDetailsDialogOpen}
            />
        </>
    );
};